import { get } from "lodash";
import {
    conditionTypes,
    propertyClassTypes,
    propertyQualityTypes,
} from "../../create-property/definitions";
import { yearBuiltOptions } from "../../../utils/helperUtils";

const generateCells = (data: any, spaceId: any, pathsWithOptions: any[], size: number) => {
    const subject = pathsWithOptions.map(({ path, options }) =>
        options.withSubject === false
            ? {
                  type: "ignore",
              }
            : {
                  ...options,
                  key: `appealDetails.appealApproachCalculationData.income.spaces.${spaceId}.leaseAdjustment.subject.${path}`,
                  value: get(
                      data,
                      `appealDetails.appealApproachCalculationData.income.spaces.${spaceId}.leaseAdjustment.subject.${path}`,
                      ""
                  ),
                  imageObjectKey: options.imageObjectKey
                      ? `appealDetails.appealApproachCalculationData.income.spaces.${spaceId}.leaseAdjustment.subject.${options.imageObjectKey}`
                      : undefined,
              }
    );

    const comps = Array.from({ length: size }, (_, index) =>
        pathsWithOptions.map(({ path, options }) => ({
            ...options,
            key: `appealDetails.appealApproachCalculationData.income.spaces.${spaceId}.leaseAdjustment.comps.${index}.${path}`,
            value: get(
                data,
                `appealDetails.appealApproachCalculationData.income.spaces.${spaceId}.leaseAdjustment.comps.${index}.${path}`,
                ""
            ),

            imageObjectKey: options.imageObjectKey
                ? `appealDetails.appealApproachCalculationData.income.spaces.${spaceId}.leaseAdjustment.comps.${index}.${options.imageObjectKey}`
                : undefined,
        }))
    );

    return [subject, ...comps];
};

const getToggleValues = (size: number, spaceId: any) => {
    return Object.fromEntries(
        Array.from({ length: size }, (_, index) => [
            index + 1,
            `appealDetails.appealApproachCalculationData.income.spaces.${spaceId}.leaseAdjustment.comps.${index}.enable`,
        ])
    );
};

export const INITIAL_INCOME_APPROACH_TABLE_DATA_DEFINITION = (data: any, spaceId: any) => {
    const size = Object.keys(
        data.appealDetails.appealApproachCalculationData.income.spaces[spaceId].leaseAdjustment
            .comps
    ).length;

    return {
        headers: ["Subject", ...Array.from({ length: size }, (_, index) => `Comp #${index + 1}`)],
        toggles: getToggleValues(size, spaceId),
        sections: [
            {
                title: "Property Characteristics",
                key: "propertyCharacteristics",
                rows: [
                    {
                        label: "Street Address",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "address.streetaddress",
                                    options: {
                                        type: "text",
                                        editable: false,
                                        imageObjectKey: "addressS3Object",
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "City, county and State",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "address.city",
                                    options: { type: "inputString", editable: true },
                                },
                                {
                                    path: "address.county",
                                    options: { type: "inputString", editable: true, font: "b100" },
                                },
                                {
                                    path: "address.state",
                                    options: { type: "inputString", editable: true, font: "b100" },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Parcel number",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "parcelNumber",
                                    options: {
                                        withSubject: true,
                                        type: "inputString",
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Building type",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "propertyType",
                                    options: {
                                        withSubject: true,
                                        type: "inputString",
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Use",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "propertyType",
                                    options: {
                                        withSubject: true,
                                        type: "inputString",
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Building size (sqft)",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "buildingSizeSqFt",
                                    options: {
                                        withSubject: true,
                                        type: "inputNumber",
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Land size (sqft)",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "landSizeSqFt",
                                    options: {
                                        withSubject: true,
                                        type: "inputNumber",
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Land to building ratio",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "landToBuildingRatio",
                                    options: {
                                        withSubject: true,
                                        type: "inputNumber",
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Year built (effective)",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "yearBuilt",
                                    options: {
                                        type: "dropdown",
                                        options: [...yearBuiltOptions],
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Effective year",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "effectiveYear",
                                    options: {
                                        type: "dropdown",
                                        options: [...yearBuiltOptions],
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Rental class",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "rentalClass",
                                    options: {
                                        type: "dropdown",
                                        options: [...propertyClassTypes],
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Construction Class",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "constructionClass",
                                    options: {
                                        type: "dropdown",
                                        options: [...propertyClassTypes],
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Quality of construction",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "qualityOfConstruction",
                                    options: {
                                        type: "dropdown",
                                        options: [...propertyQualityTypes],
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Condition",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "condition",
                                    options: {
                                        type: "dropdown",
                                        options: [...conditionTypes],
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Office %",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "officePercentage",
                                    options: {
                                        withSubject: true,
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: "%",
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Average story (clear) height",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "averageStoryHeight",
                                    options: {
                                        withSubject: true,
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: " ft",
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Comments",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "appealComments",
                                    options: { type: "comment", editable: true },
                                },
                            ],
                            size
                        ),
                    },
                ],
            },
            {
                title: "Tenet Information",
                key: "tenetInformation",
                rows: [
                    {
                        label: "Lease structure",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "tenantInformation.leaseStructure",
                                    options: {
                                        withSubject: false,
                                        type: "dropdown",
                                        options: [
                                            { label: "NNN", value: "NNN" },
                                            { label: "FS", value: "FS" },
                                            { label: "Modified", value: "Modified" },
                                            { label: "Other", value: "Other" },
                                        ],
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Date of lease",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "tenantInformation.dateOfLease",
                                    options: { withSubject: false, type: "date", editable: true },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Lease term (months)",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "tenantInformation.leaseTerm",
                                    options: {
                                        withSubject: false,
                                        type: "inputNumber",
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Building Size (sqft)",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "tenantInformation.buildingSizeSqFt",
                                    options: {
                                        withSubject: false,
                                        type: "inputNumber",
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Leased area (sqft)",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "tenantInformation.leasedArea",
                                    options: {
                                        withSubject: false,
                                        type: "inputNumber",
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Free rent (months)",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "tenantInformation.freeRent",
                                    options: {
                                        withSubject: false,
                                        type: "inputNumber",
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Unadjusted lease rate Mo/sqft",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "tenantInformation.unadjustedLeaseRate",
                                    options: {
                                        withSubject: false,
                                        type: "inputNumber",
                                        editable: true,
                                        prefix: "$",
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Effective unadjusted lease rate per year/sqft",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "tenantInformation.effectiveUnadjustedLeaseRate",
                                    options: {
                                        withSubject: false,
                                        type: "inputNumber",
                                        editable: true,
                                        prefix: "$",
                                    },
                                },
                            ],
                            size
                        ),
                    },
                ],
            },
            {
                title: "Adjustments",
                key: "adjustments",
                rows: [
                    {
                        label: "Conditions / Terms of lease",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "adjustments.conditionsTermsOfLease.rate",
                                    options: {
                                        type: "inputNumber",
                                        suffix: "%",
                                        editable: true,
                                        withSubject: false,
                                    },
                                },
                                {
                                    path: "adjustments.conditionsTermsOfLease.adjustedPrice",
                                    options: {
                                        type: "text",
                                        font: "b100",
                                        editable: false,
                                        prefix: "Adjusted price: $",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Lease structure",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "adjustments.leaseStructure.rate",
                                    options: {
                                        type: "inputNumber",
                                        suffix: "%",
                                        editable: true,
                                        withSubject: false,
                                    },
                                },
                                {
                                    path: "adjustments.leaseStructure.adjustedPrice",
                                    options: {
                                        type: "text",
                                        font: "b100",
                                        editable: false,
                                        prefix: "Adjusted price: $",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Market conditions",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "adjustments.marketConditions.rate",
                                    options: {
                                        type: "inputNumber",
                                        suffix: "%",
                                        editable: true,
                                        withSubject: false,
                                    },
                                },
                                {
                                    path: "adjustments.marketConditions.adjustedPrice",
                                    options: {
                                        type: "text",
                                        font: "b100",
                                        editable: false,
                                        prefix: "Adjusted price: $",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                ],
            },
            {
                title: "Other Adjustments",
                key: "otherAdjustments",
                rows: [
                    {
                        label: "Location",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "otherAdjustments.location",
                                    options: {
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Size",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "otherAdjustments.size",
                                    options: {
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Age/Condition",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "otherAdjustments.ageCondition",
                                    options: {
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Quality/Functional utility",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "otherAdjustments.qualityFunctionalUtility",
                                    options: {
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Office/Finished area",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "otherAdjustments.officeFinishedArea",
                                    options: {
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Land to building ratio",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "otherAdjustments.landToBuildingRatio",
                                    options: {
                                        type: "inputNumber",
                                        editable: false,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Story height",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "otherAdjustments.storyHeight",
                                    options: {
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Other adjustments",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "otherAdjustments.otherAdjustments",
                                    options: {
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Net adjustments",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "otherAdjustments.netAdjustments",
                                    options: {
                                        type: "inputNumber",
                                        editable: false,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Gross adjustments",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "otherAdjustments.grossAdjustments",
                                    options: {
                                        type: "inputNumber",
                                        editable: false,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Adjusted price / sqft",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "adjustedPricePerSqFt",
                                    options: {
                                        type: "text",
                                        editable: false,
                                        prefix: "$",
                                        font: "h100",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Weight",
                        cells: generateCells(
                            data,
                            spaceId,
                            [
                                {
                                    path: "weight",
                                    options: {
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                ],
            },
        ],
    };
};
